/**
 * Comprehensive Sentry Service for RealSeats SAPUI5 Application
 *
 * This service provides a centralized interface for all Sentry operations including:
 * - Error tracking and exception handling
 * - Performance monitoring and tracing
 * - User context and session management
 * - Structured logging with breadcrumbs
 * - API call instrumentation
 * - UI interaction tracking
 *
 * Usage Examples:
 *
 * // Error Tracking
 * SentryService.captureError(error, { context: 'booking_creation' });
 *
 * // Performance Monitoring
 * SentryService.startSpan('ui.interaction', 'Button Click', () => {
 *   this.handleClick();
 * });
 *
 * // Structured Logging
 * const logger = SentryService.createLogger('BookingController');
 * logger.info('Booking created', { bookingId: '123' });
 *
 * @namespace de.realcore.realseats.services
 */

// Extend the global Sentry interface to include all features we use
declare global {
    // eslint-disable-next-line no-unused-vars
    interface Window {
        Sentry: any;
    }
}

interface LogLevel {
    trace: "trace";
    debug: "debug";
    info: "info";
    warn: "warn";
    error: "error";
    fatal: "fatal";
}

interface SentryLogger {
    trace(message: string, extra?: Record<string, any>): void;
    debug(message: string, extra?: Record<string, any>): void;
    info(message: string, extra?: Record<string, any>): void;
    warn(message: string, extra?: Record<string, any>): void;
    error(message: string, extra?: Record<string, any>): void;
    fatal(message: string, extra?: Record<string, any>): void;
    fmt: (strings: TemplateStringsArray, ...values: any[]) => string;
}

export default class SentryService {
    private static isInitialized = false;
    private static readonly loggers: Map<string, SentryLogger> = new Map();

    /**
     * Initialize Sentry service
     * Called automatically when Sentry is loaded in index.html
     */
    public static initialize(): void {
        if (this.isInitialized || !window.Sentry) {
            return;
        }

        this.isInitialized = true;

        // Add global error handler for unhandled promises
        window.addEventListener("unhandledrejection", (event) => {
            this.captureError(new Error(event.reason), {
                type: "unhandled_promise_rejection",
                reason: event.reason
            });
        });

        console.log("SentryService initialized successfully");
    }

    /**
     * Check if Sentry is available and initialized
     */
    public static isAvailable(): boolean {
        return !!(
            window.Sentry &&
            typeof window.Sentry.captureException === "function" &&
            this.isInitialized
        );
    }

    /**
     * Capture an error with context information
     *
     * @param error The error to capture
     * @param context Additional context information
     * @param tags Optional tags for categorization
     * @param level Error level (default: 'error')
     *
     * @example
     * SentryService.captureError(error, {
     *   operation: 'create_booking',
     *   bookingData: { seatId: 'A1' }
     * }, {
     *   'booking.operation': 'create'
     * });
     */
    public static captureError(
        error: Error,
        context?: Record<string, any>,
        tags?: Record<string, string>,
        level: "error" | "warn" | "info" = "error"
    ): void {
        if (!this.isAvailable()) {
            console.error("Sentry not available:", error);
            return;
        }

        try {
            // Set context if provided
            if (context) {
                window.Sentry.setContext("error_context", context);
            }

            // Set tags if provided
            if (tags) {
                Object.entries(tags).forEach(([key, value]) => {
                    window.Sentry.setTag(key, value);
                });
            }

            // Capture the error
            window.Sentry.captureException(error, {
                level,
                extra: context,
                tags
            });

            // Add breadcrumb for the error
            this.addBreadcrumb(`Error captured: ${error.message}`, "error", level, {
                errorName: error.name,
                errorStack: error.stack?.substring(0, 500) // Limit stack trace length
            });
        } catch (sentryError) {
            console.error("Failed to capture error in Sentry:", sentryError);
        }
    }

    /**
     * Start a performance span with automatic cleanup
     *
     * @param operation The operation type (e.g., 'http.client', 'ui.interaction')
     * @param name Descriptive name for the span
     * @param fn Function to execute within the span
     * @param attributes Optional attributes to add to the span
     * @returns Result of the function execution
     *
     * @example
     * const result = SentryService.startSpan('ui.interaction', 'Button Click', (span) => {
     *   span?.setAttribute('button.id', 'submit-btn');
     *   return this.processClick();
     * });
     */
    public static startSpan<T>(
        operation: string,
        name: string,
        fn: (span?: any) => T,
        attributes?: Record<string, string | number | boolean>
    ): T {
        if (!this.isAvailable()) {
            return fn();
        }

        try {
            return window.Sentry.startSpan(
                {
                    op: operation,
                    name: name
                },
                (span: any) => {
                    // Add attributes if provided
                    if (attributes && span) {
                        Object.entries(attributes).forEach(([key, value]) => {
                            span.setAttribute(key, value);
                        });
                    }

                    // Add breadcrumb for the span
                    this.addBreadcrumb(`Started span: ${name}`, "performance", "info", {
                        operation,
                        attributes
                    });

                    return fn(span);
                }
            );
        } catch (error) {
            console.error("Failed to create Sentry span:", error);
            return fn();
        }
    }

    /**
     * Instrument API calls with automatic tracing and error handling
     *
     * @param method HTTP method
     * @param url API endpoint URL
     * @param fn Function that makes the API call
     * @param requestData Optional request data for context
     * @returns Result of the API call
     *
     * @example
     * const bookings = await SentryService.instrumentApiCall(
     *   'GET',
     *   '/api/booking/me',
     *   () => this.fetchMyBookings(),
     *   { userId: currentUser.id }
     * );
     */
    public static async instrumentApiCall<T>(
        method: string,
        url: string,
        fn: () => Promise<T>,
        requestData?: Record<string, any>
    ): Promise<T> {
        const spanName = `${method} ${url}`;
        const startTime = Date.now();

        // Add breadcrumb for API call start
        this.addBreadcrumb(`API call started: ${spanName}`, "http", "info", {
            method,
            url,
            requestData
        });

        return this.startSpan("http.client", spanName, async (span) => {
            try {
                // Set span attributes
                if (span) {
                    span.setAttribute("http.method", method);
                    span.setAttribute("http.url", url);
                    if (requestData) {
                        span.setAttribute("http.request_size", JSON.stringify(requestData).length);
                    }
                }

                const result = await fn();
                const duration = Date.now() - startTime;

                // Add success breadcrumb
                this.addBreadcrumb(`API call completed: ${spanName}`, "http", "info", {
                    method,
                    url,
                    duration: `${duration}ms`,
                    success: true
                });

                // Set success attributes
                if (span) {
                    span.setAttribute("http.status_code", 200);
                    const responseSize = result !== undefined ? JSON.stringify(result).length : 0;
                    span.setAttribute("http.response_size", responseSize);
                }

                return result;
            } catch (error) {
                const duration = Date.now() - startTime;
                const errorMessage = error instanceof Error ? error.message : "Unknown error";

                // Add error breadcrumb
                this.addBreadcrumb(`API call failed: ${spanName}`, "http", "error", {
                    method,
                    url,
                    duration: `${duration}ms`,
                    error: errorMessage
                });

                // Set error attributes
                if (span) {
                    span.setAttribute("http.status_code", this.extractStatusCode(error));
                    span.setAttribute("error", true);
                }

                // Capture the error with API context
                this.captureError(
                    error as Error,
                    {
                        api_call: {
                            method,
                            url,
                            duration,
                            requestData
                        }
                    },
                    {
                        "api.method": method,
                        "api.endpoint": url,
                        "api.status": "failed"
                    }
                );

                throw error;
            }
        });
    }

    /**
     * Instrument UI interactions with performance tracking
     *
     * @param elementType Type of element (button, link, etc.)
     * @param action Action performed (click, hover, etc.)
     * @param fn Function to execute
     * @param elementId Optional element identifier
     * @param metadata Optional additional metadata
     * @returns Result of the function execution
     *
     * @example
     * SentryService.instrumentUIInteraction(
     *   'button',
     *   'click',
     *   () => this.handleSubmit(),
     *   'submit-button',
     *   { formValid: true }
     * );
     */
    public static instrumentUIInteraction<T>(
        elementType: string,
        action: string,
        fn: () => T,
        elementId?: string,
        metadata?: Record<string, any>
    ): T {
        const elementSuffix = elementId ? ` (${elementId})` : "";
        const spanName = `${elementType}.${action}${elementSuffix}`;

        return this.startSpan("ui.interaction", spanName, (span) => {
            // Set span attributes
            if (span) {
                span.setAttribute("ui.element_type", elementType);
                span.setAttribute("ui.action", action);
                if (elementId) {
                    span.setAttribute("ui.element_id", elementId);
                }
                if (metadata) {
                    Object.entries(metadata).forEach(([key, value]) => {
                        span.setAttribute(`ui.${key}`, String(value));
                    });
                }
            }

            // Add breadcrumb
            this.addBreadcrumb(`UI interaction: ${spanName}`, "ui", "info", {
                elementType,
                action,
                elementId,
                metadata
            });

            return fn();
        });
    }

    /**
     * Set user context information
     *
     * @param user User information
     *
     * @example
     * SentryService.setUser({
     *   id: user.id,
     *   username: user.username,
     *   email: user.email
     * });
     */
    public static setUser(user: {
        id?: string;
        username?: string;
        email?: string;
        name?: string;
        [key: string]: any;
    }): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            window.Sentry.setUser(user);

            // Add breadcrumb for user context update
            this.addBreadcrumb("User context updated", "auth", "info", {
                userId: user.id,
                username: user.username
            });
        } catch (error) {
            console.error("Failed to set Sentry user context:", error);
        }
    }

    /**
     * Set custom tags for event categorization
     *
     * @param tags Key-value pairs of tags
     *
     * @example
     * SentryService.setTags({
     *   'feature': 'booking',
     *   'component': 'seat-selection'
     * });
     */
    public static setTags(tags: Record<string, string>): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            Object.entries(tags).forEach(([key, value]) => {
                window.Sentry.setTag(key, value);
            });
        } catch (error) {
            console.error("Failed to set Sentry tags:", error);
        }
    }

    /**
     * Set additional context information
     *
     * @param key Context key
     * @param context Context data
     *
     * @example
     * SentryService.setContext('booking_flow', {
     *   step: 'seat_selection',
     *   progress: '50%'
     * });
     */
    public static setContext(key: string, context: Record<string, any>): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            window.Sentry.setContext(key, context);
        } catch (error) {
            console.error("Failed to set Sentry context:", error);
        }
    }

    /**
     * Add a breadcrumb for debugging
     *
     * @param message Descriptive message
     * @param category Category of the breadcrumb
     * @param level Severity level
     * @param data Additional data
     *
     * @example
     * SentryService.addBreadcrumb(
     *   'User selected seat A1',
     *   'user_action',
     *   'info',
     *   { seatId: 'A1', roomId: 'R1' }
     * );
     */
    public static addBreadcrumb(
        message: string,
        category: string = "custom",
        level: "trace" | "debug" | "info" | "warn" | "error" | "fatal" = "info",
        data?: Record<string, any>
    ): void {
        if (!this.isAvailable()) {
            return;
        }

        try {
            window.Sentry.addBreadcrumb({
                message,
                category,
                level,
                data,
                timestamp: Date.now() / 1000
            });
        } catch (error) {
            console.error("Failed to add Sentry breadcrumb:", error);
        }
    }

    /**
     * Create a component-specific logger
     *
     * @param componentName Name of the component
     * @returns Logger instance
     *
     * @example
     * const logger = SentryService.createLogger('BookingController');
     * logger.info('Booking process started', { seatId: 'A1' });
     */
    public static createLogger(componentName: string): SentryLogger {
        const existingLogger = this.loggers.get(componentName);
        if (existingLogger) {
            return existingLogger;
        }

        const logger: SentryLogger = {
            trace: (message: string, extra?: Record<string, any>) =>
                this.log("trace", componentName, message, extra),
            debug: (message: string, extra?: Record<string, any>) =>
                this.log("debug", componentName, message, extra),
            info: (message: string, extra?: Record<string, any>) =>
                this.log("info", componentName, message, extra),
            warn: (message: string, extra?: Record<string, any>) =>
                this.log("warn", componentName, message, extra),
            error: (message: string, extra?: Record<string, any>) =>
                this.log("error", componentName, message, extra),
            fatal: (message: string, extra?: Record<string, any>) =>
                this.log("fatal", componentName, message, extra),
            fmt: (strings: TemplateStringsArray, ...values: any[]): string => {
                return strings.reduce((result, string, i) => {
                    return result + string + (values[i] || "");
                }, "");
            }
        };

        this.loggers.set(componentName, logger);
        return logger;
    }

    /**
     * Internal logging method
     */
    private static log(
        level: keyof LogLevel,
        componentName: string,
        message: string,
        extra?: Record<string, any>
    ): void {
        const formattedMessage = `[${componentName}] ${message}`;
        const consoleLevel = level === "fatal" ? "error" : level;

        // Always log to console
        console[consoleLevel](formattedMessage, extra);

        if (!this.isAvailable()) {
            return;
        }

        try {
            // Add as breadcrumb for important logs
            if (["warn", "error", "fatal"].includes(level)) {
                this.addBreadcrumb(
                    formattedMessage,
                    "log",
                    level as "warn" | "error" | "fatal",
                    extra
                );
            }
        } catch (error) {
            console.error("Failed to add log breadcrumb to Sentry:", error);
        }
    }

    /**
     * Extract HTTP status code from error
     */
    private static extractStatusCode(error: any): number {
        if (error?.response?.status) {
            return error.response.status;
        }
        if (error?.status) {
            return error.status;
        }
        if (error?.message?.includes("404")) {
            return 404;
        }
        if (error?.message?.includes("401")) {
            return 401;
        }
        if (error?.message?.includes("403")) {
            return 403;
        }
        if (error?.message?.includes("500")) {
            return 500;
        }
        return 0; // Unknown status
    }

    /**
     * Test function for verifying Sentry integration
     * Should only be used in development
     */
    public static testIntegration(): void {
        if (!this.isAvailable()) {
            console.error("Sentry is not available for testing");
            return;
        }

        console.log("Testing Sentry integration...");

        // Test error capture
        this.captureError(new Error("Test error from SentryService"), {
            test: true,
            timestamp: new Date().toISOString()
        });

        // Test performance span
        this.startSpan("test.operation", "Sentry Integration Test", () => {
            console.log("Test span executed successfully");
        });

        // Test breadcrumb
        this.addBreadcrumb("Sentry integration test completed", "test", "info");

        console.log("Sentry integration test completed");
    }
}

// Initialize the service when the module is loaded
if (typeof window !== "undefined") {
    // Wait for Sentry to be available
    const checkSentry = () => {
        if (window.Sentry) {
            SentryService.initialize();
        } else {
            setTimeout(checkSentry, 100);
        }
    };
    checkSentry();
}
